/**
 * 貼り付けからの一括追加＝プレビュー（修正画面）の純ロジック。
 *
 * 土台（`41f0c70`）の pastedLines.ts（列判定）と
 * `GET /purchase-orders/variants?codes=`（一括突合）の間を埋める:
 * 貼り付けグリッド × サーバーが返した商品 → 行ごとの候補・数量・除外状態。
 *
 * 🔴 コード → 商品の対応付けは【barcode 優先】＝スキャン・サーバー側
 *    `lookup_variants_by_codes` と同じ規則。ここで SKU を先に見ると
 *    「スキャンで引ける商品が貼り付けでは別商品」という食い違いが再発する。
 * 🔴 突合できなかった行は黙って落とさない＝`unmatched` として返し、画面が
 *    件数と内容を見せる。
 */

import type { PurchaseOrderLine } from "../api/purchase_orders";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../api/stocky_public_ids";
import {
  detectPasteColumns,
  parseQuantity,
  type PastedGrid,
} from "./pastedLines";

/** プレビューの1行＝貼り付けの1行（見出し行を除く）。 */
export interface PastePreviewRow {
  /** body 内の行番号（React key 用・行の同一性）。 */
  key: number;
  /** 商品コード列のセル値（trim 済み）。 */
  code: string;
  /** 貼り付け行の生セル。数量列を選び直したとき読み直すために保持する。 */
  cells: readonly string[];
  /** コードに一致した商品（barcode 優先）。常に1件以上。 */
  candidates: readonly PurchaseOrderLine[];
  /** 追加対象として選択中の variant。複数候補のときは画面が選び直せる。 */
  selectedVariantId: StockyPublicBigIntId;
  /** 数量の入力バッファ（直接編集できる）。 */
  qtyInput: string;
  /** 追加対象か（行の除外）。 */
  included: boolean;
  /** すでに明細にある＝追加できない（除外固定・理由を画面に出す）。 */
  alreadyInOrder: boolean;
}

export interface PastePreviewData {
  hasHeader: boolean;
  /** 見出し行（数量列セレクタのラベル用）。見出しが無ければ null。 */
  headerRow: readonly string[] | null;
  codeColumn: number;
  qtyColumn: number | null;
  /** 数量になり得る列（選び直しの選択肢）。 */
  qtyCandidates: readonly number[];
  rows: readonly PastePreviewRow[];
  /** 突合できなかった行の表示用テキスト（黙って落とさない）。 */
  unmatched: readonly string[];
}

/** まとめて追加する1件。親（PurchaseOrders）が addedLines と数量編集へ反映する。 */
export interface PastedProductAdd {
  line: PurchaseOrderLine;
  quantity: number;
}

/**
 * lookup へ投げるセル値（trim 済み・空を除き・重複除去）。
 * 🔴 全列の値を投げる＝どの列が商品コードかはカタログ突合で決める（列順に頼らない）。
 *    backend の `MAX_LOOKUP_CODES` は行数 × 列数の余裕を見て決められている。
 */
export function collectPasteCodes(grid: PastedGrid): string[] {
  const codes = new Set<string>();
  for (const row of grid) {
    for (const cell of row) {
      const value = cell.trim();
      if (value !== "") codes.add(value);
    }
  }
  return [...codes];
}

/**
 * コード → 候補商品（barcode 優先）。サーバーが実在すると答えた行だけから引く。
 * barcode で1件でも当たったコードは SKU 側を見ない＝サーバーの解決規則の写し。
 */
export function candidatesByCode(
  codes: readonly string[],
  lines: readonly PurchaseOrderLine[],
): Map<string, PurchaseOrderLine[]> {
  const byBarcode = new Map<string, PurchaseOrderLine[]>();
  const bySku = new Map<string, PurchaseOrderLine[]>();
  for (const line of lines) {
    const barcode = line.barcode?.trim();
    if (barcode) {
      byBarcode.set(barcode, [...(byBarcode.get(barcode) ?? []), line]);
    }
    const sku = line.sku?.trim();
    if (sku) {
      bySku.set(sku, [...(bySku.get(sku) ?? []), line]);
    }
  }
  const result = new Map<string, PurchaseOrderLine[]>();
  for (const code of codes) {
    const matches = byBarcode.get(code) ?? bySku.get(code);
    if (matches) result.set(code, matches);
  }
  return result;
}

/** 選択中の数量列からその行の数量を読む。読めなければ 1 に倒す（画面で直せる）。 */
export function qtyInputFor(
  cells: readonly string[],
  qtyColumn: number | null,
): string {
  if (qtyColumn === null) return "1";
  const parsed = parseQuantity(cells[qtyColumn] ?? "");
  return parsed !== null ? String(parsed) : "1";
}

/**
 * 貼り付けグリッドとサーバー応答からプレビューを組み立てる。
 * 商品コードの列が特定できなければ null（呼び出し側が案内を出す）。
 */
export function buildPastePreview(
  grid: PastedGrid,
  lines: readonly PurchaseOrderLine[],
  existingVariantIds: ReadonlySet<StockyPublicBigIntId>,
): PastePreviewData | null {
  const canonicalExistingVariantIds = new Set(
    [...existingVariantIds].map(canonicalStockyPublicBigIntId),
  );
  const codes = collectPasteCodes(grid);
  const candidates = candidatesByCode(codes, lines);
  const detection = detectPasteColumns(grid, new Set(candidates.keys()));
  const codeColumn = detection.codeColumn;
  if (codeColumn === null) return null;

  const body = detection.hasHeader ? grid.slice(1) : grid;
  const rows: PastePreviewRow[] = [];
  const unmatched: string[] = [];
  body.forEach((cells, index) => {
    const code = (cells[codeColumn] ?? "").trim();
    const rowCandidates = code === "" ? undefined : candidates.get(code);
    if (rowCandidates === undefined) {
      // 🔴 黙って落とさない＝コード列が空の行は行全体を、そうでなければコードを見せる。
      const shown =
        code !== ""
          ? code
          : cells
              .map((cell) => cell.trim())
              .filter((cell) => cell !== "")
              .join(" ");
      if (shown !== "") unmatched.push(shown);
      return;
    }
    // 既に明細にある variant は既定の選択から避ける。全候補が明細済みなら追加不可。
    const free = rowCandidates.find(
      (candidate) =>
        !canonicalExistingVariantIds.has(
          canonicalStockyPublicBigIntId(candidate.variant_id),
        ),
    );
    const selected = free ?? rowCandidates[0];
    rows.push({
      key: index,
      code,
      cells,
      candidates: rowCandidates,
      selectedVariantId: selected.variant_id,
      qtyInput: qtyInputFor(cells, detection.qtyColumn),
      included: free !== undefined,
      alreadyInOrder: free === undefined,
    });
  });

  return {
    hasHeader: detection.hasHeader,
    headerRow: detection.hasHeader ? grid[0] : null,
    codeColumn,
    qtyColumn: detection.qtyColumn,
    qtyCandidates: detection.qtyCandidates,
    rows,
    unmatched,
  };
}

/**
 * 数量列を選び直す＝全行の数量を選んだ列から読み直す。
 * 行の除外・候補の選択は保つ（列の選び直しは「数量の読み違い」の修正なので）。
 */
export function withQtyColumn(
  preview: PastePreviewData,
  qtyColumn: number | null,
): PastePreviewData {
  return {
    ...preview,
    qtyColumn,
    rows: preview.rows.map((row) => ({
      ...row,
      qtyInput: qtyInputFor(row.cells, qtyColumn),
    })),
  };
}

/**
 * 数量入力の解釈。空欄は 1（コードだけを貼った場合の既定）。
 * 不正値は null ＝呼び出し側が行エラーを出し「まとめて追加」を止める。
 */
export function parseRowQty(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === "") return 1;
  return parseQuantity(trimmed);
}

/**
 * まとめて追加する明細。同じ商品に解決した行は数量を合算する
 * （注文書の明細は variant ごとに1行＝2行には分けられない）。
 * 除外行・明細済み・数量不正の行は含めない（不正は画面側で先にブロック済み）。
 */
export function collectAddItems(
  rows: readonly PastePreviewRow[],
  existingVariantIds: ReadonlySet<StockyPublicBigIntId>,
): PastedProductAdd[] {
  const canonicalExistingVariantIds = new Set(
    [...existingVariantIds].map(canonicalStockyPublicBigIntId),
  );
  const byVariant = new Map<string, PastedProductAdd>();
  for (const row of rows) {
    if (!row.included || row.alreadyInOrder) continue;
    const line = row.candidates.find(
      (candidate) =>
        canonicalStockyPublicBigIntId(candidate.variant_id) ===
        canonicalStockyPublicBigIntId(row.selectedVariantId),
    );
    if (
      line === undefined ||
      canonicalExistingVariantIds.has(
        canonicalStockyPublicBigIntId(line.variant_id),
      )
    )
      continue;
    const quantity = parseRowQty(row.qtyInput);
    if (quantity === null) continue;
    const canonicalId = canonicalStockyPublicBigIntId(line.variant_id);
    const current = byVariant.get(canonicalId);
    byVariant.set(
      canonicalId,
      current === undefined
        ? { line, quantity }
        : { ...current, quantity: current.quantity + quantity },
    );
  }
  return [...byVariant.values()];
}
